import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import Resizer from 'react-image-file-resizer';

const ImageUpload = ({ value = [], onChange, multiple = false, maxFiles = 5 }) => {
  const [previews, setPreviews] = useState(value);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const resizeImage = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1200, // maxWidth
        1200, // maxHeight
        'JPEG', // compressFormat
        85, // quality
        0, // rotation
        (uri) => {
          resolve(uri);
        },
        'base64' // outputType
      );
    });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const resizedImages = await Promise.all(
        files.map((file) => resizeImage(file))
      );

      let newPreviews;
      if (multiple) {
        newPreviews = [...previews, ...resizedImages].slice(0, maxFiles);
      } else {
        newPreviews = [resizedImages[0]];
      }

      setPreviews(newPreviews);
      onChange(newPreviews);
    } catch (error) {
      console.error('Error processing images:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    onChange(newPreviews);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        {previews.map((preview, index) => (
          <div key={index} className="relative group">
            <img
              src={preview}
              alt={`Upload ${index + 1}`}
              className="w-24 h-24 object-cover rounded border border-gray-200"
            />
            <button
              type="button"
              onClick={() => removeImage(index)}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}

        {(multiple ? previews.length < maxFiles : previews.length === 0) && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={multiple}
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 flex flex-col items-center justify-center"
            >
              {uploading ? (
                <div className="animate-spin">⏳</div>
              ) : (
                <>
                  <Upload className="h-6 w-6 mb-2" />
                  <span className="text-xs">Upload</span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <p className="text-sm text-gray-600">
        {multiple
          ? `Upload up to ${maxFiles} images. Images will be resized automatically.`
          : 'Upload one image. It will be resized automatically.'}
      </p>
    </div>
  );
};

export default ImageUpload;
